import { useForm, Controller } from 'react-hook-form';
import { Button } from 'components/button';
import type {
  CreateDosageScheduleMutation,
  CreateDosageScheduleMutationVariables,
  CreateDosageSchedulePageQuery,
  VariantSequenceNode,
} from 'graphql/types';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { buildRoute } from 'utils/routes';
import { useNotifications } from 'notifications';
import { Dropdown } from 'components/dropdown-v7';
import { Input } from 'components/input';
import { Mermaid } from 'components/mermaid';
import { useCallback, useMemo, useState } from 'react';
import {
  DosageScheduleEditor,
  doseFragment,
} from 'components/dosage-schedule-editor/editor';
import type { Dose } from 'components/dosage-schedule-editor/node';

type CreateDosageScheduleForm = {
  internalName: string;
  displayName: string;
  sequenceId: string;
  maxDaysSinceDose: number;
  minIntervalDays: number;
  doses: Dose[];
};

const CreateDosageSchedulePage = (): JSX.Element => {
  const history = useHistory();
  const showNotification = useNotifications();
  const [pendingScheduleChanges, setPendingScheduleChanges] = useState(false);
  // These useCallbacks are annoying, but required to avoid multiple calls to
  // these functions on a rerender of CreateDosageSchedulePage
  const setPendingScheduleChangesTrue = useCallback(
    () => setPendingScheduleChanges(true),
    [],
  );
  const setPendingScheduleChangesFalse = useCallback(
    () => setPendingScheduleChanges(false),
    [],
  );

  const { handleSubmit, register, formState, control, watch } =
    useForm<CreateDosageScheduleForm>({
      reValidateMode: 'onChange',
    });

  const { data } = useSuspenseQuery<CreateDosageSchedulePageQuery>(
    gql`
      query CreateDosageSchedulePage {
        sequences(
            problemTypes: [WEIGHT_LOSS],
            requiresPrescription: true
        ) {
          id
          internalName
          status
          mermaid
          nodes {
            id
            ... on VariantSequenceNode {
              quantity
              variant {
                id
                name
                product {
                  id
                  name
                  productType
                }
              }
            }
          }
        }
        doses {
          ...doseData
        }
      }
      ${doseFragment},
    `,
  );

  const sequenceId = watch().sequenceId;
  const selectedSequence = useMemo(
    () => data.sequences?.find((s) => s.id === sequenceId),
    [sequenceId, data.sequences],
  );
  const mermaid = selectedSequence?.mermaid;
  const nodeVariants = selectedSequence?.nodes
    .filter(
      (n): n is VariantSequenceNode => n.__typename === 'VariantSequenceNode',
    )
    .map((n) => n.variant);

  const variants: Parameters<typeof DosageScheduleEditor>[0]['variants'] = [];
  const seenVariantIds = new Set<string>();

  for (const v of nodeVariants ?? []) {
    if (v && v.product.productType === 'RX' && !seenVariantIds.has(v.id)) {
      variants.push({
        id: v.id,
        name: v.name,
        productId: v.product.id,
        productName: v.product.name,
        sku: v.inventory?.sku || '',
      });
      seenVariantIds.add(v.id);
    }
  }

  const [createDosageSchedule] = useMutation<
    CreateDosageScheduleMutation,
    CreateDosageScheduleMutationVariables
  >(
    gql`
      mutation CreateDosageSchedule($input: CreateDosageScheduleInput!) {
        createDosageSchedule(input: $input) {
          dosageSchedule {
            id
            maxDaysSinceDoseBeforeIntervention
            minSafeDoseIntervalDays
            internalName
            displayName
            doses {
              id
              intervalDays
              nextDosageScheduleEventId
              dose {
                ...doseData
              }
            }
          }
        }
      }
      ${doseFragment},
    `,
    {
      onCompleted: (data) => {
        history.push(
          buildRoute.dosageSchedule(
            data.createDosageSchedule.dosageSchedule.id,
          ),
        );

        showNotification({
          type: 'success',
          message: 'dosage schedule created successfully',
        });
      },
      onError: () => {
        showNotification({
          type: 'error',
          message: 'Error creating dosage schedule',
        });
      },
    },
  );

  const submitHandler = async (
    formValues: CreateDosageScheduleForm,
  ): Promise<void> => {
    const input: CreateDosageScheduleMutationVariables['input'] = {
      internalName: formValues.internalName,
      displayName: formValues.displayName,
      maxDaysSinceDoseBeforeIntervention: Number(formValues.maxDaysSinceDose),
      minSafeDoseIntervalDays: Number(formValues.minIntervalDays),
      doses: [],
    };

    for (let i = 0; i < formValues.doses.length; i++) {
      const currentDose = formValues.doses[i];

      if (!currentDose.doseId || !currentDose.variantId) {
        showNotification({
          type: 'error',
          message: 'required fields not present for dose',
        });
        return;
      }

      input.doses.push({
        id: currentDose.id,
        doseId: currentDose.doseId,
        variantId: currentDose.variantId,
        intervalDays: currentDose.nextNodeId
          ? currentDose.intervalDays
          : undefined,
        nextDosageScheduleEventId: currentDose.nextNodeId ?? undefined,
      });
    }

    await createDosageSchedule({
      variables: { input },
    });
  };

  return (
    <div className="container">
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col space-y-4 w-1/2">
            <Input
              {...register('displayName', { required: true })}
              name="displayName"
              label="Display name"
              errorMessage={formState.errors.displayName?.message}
            />
            <Input
              {...register('internalName', { required: true })}
              name="internalName"
              label="Internal name"
              errorMessage={formState.errors.internalName?.message}
            />
          </div>
          <Dropdown
            name="sequenceId"
            label="Sequence"
            control={control}
            errorMessage={formState.errors.sequenceId?.message}
            options={(data?.sequences ?? [])
              .filter((s) => s.status === 'AVAILABLE')
              .map((s) => ({
                label: s.internalName,
                value: s.id,
              }))}
          />
          {mermaid && <Mermaid key={sequenceId}>{mermaid}</Mermaid>}
          {selectedSequence && (
            <>
              <div className="heading-md py-2">Schedule</div>
              <div className="h-128 bg-white rounded shadow">
                <Controller
                  name="doses"
                  control={control}
                  render={({ field }) => {
                    const { onChange, value } = field;
                    return (
                      <DosageScheduleEditor
                        variants={variants}
                        doses={value}
                        doseOptions={data.doses ?? []}
                        onSave={onChange}
                        onDirty={setPendingScheduleChangesTrue}
                        onReset={setPendingScheduleChangesFalse}
                      />
                    );
                  }}
                />
              </div>
            </>
          )}
          <div className="w-1/2">
            <Input
              {...register('maxDaysSinceDose', { required: true })}
              tooltipProps={{
                hoverText:
                  'The maximum number of days since a previous dose was taken before MS needs to intervene.',
                icon: 'question',
              }}
              type="number"
              name="maxDaysSinceDose"
              label="Max days since previous dose before intervention"
              errorMessage={formState.errors.maxDaysSinceDose?.message}
              min={1}
            />
          </div>
          <div className="w-1/2">
            <Input
              {...register('minIntervalDays', { required: true })}
              tooltipProps={{
                hoverText: 'The smallest safe gap between doses.',
                icon: 'question',
              }}
              type="number"
              name="minIntervalDays"
              label="Min safe dose interval days"
              errorMessage={formState.errors.minIntervalDays?.message}
              min={1}
            />
          </div>
        </div>
        <div className="mt-8 w-1/4">
          <Button
            fullWidth
            type="submit"
            loading={formState.isSubmitting}
            disabled={pendingScheduleChanges}
            hoverText={
              pendingScheduleChanges
                ? 'There are changes the dosage schedule that need saving'
                : undefined
            }
          >
            Create dosage schedule
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateDosageSchedulePage;
